/**
 * Settings → Theme (native ThemePickerView): Light / Dark / System plus the
 * day/night split toggle, then the theme grid. Picks apply live, so
 * the app behind the dialog previews them.
 */
import { themes } from "@caliu/styles/themes";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnalyticsEvent, track } from "../../lib/analytics";
import { canUseTheme } from "../../lib/pro";
import { useIsPro } from "../../store/subscription";
import { useUIStore, type Theme } from "../../store/ui";
import { useIsDark } from "../../hooks/useIsDark";
import { SegmentedControl } from "../ui/SegmentedControl";
import { ThemePreviewCard } from "./ThemePreviewCard";
import {
  SettingsSection,
  SettingsSectionTitle,
  SettingsToggleRow,
  useSettingsCompact,
} from "./parts";

export function ThemeSettings() {
  const { t } = useTranslation();
  const compact = useSettingsCompact();
  const { theme, setTheme, colorThemeLight, colorThemeDark, setColorTheme, setColorThemeForMode } =
    useUIStore();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const isPro = useIsPro();
  const isDark = useIsDark();
  const activeColorTheme = isDark ? colorThemeDark : colorThemeLight;

  // Day/night pairing is opt-in: off (the default) applies a pick to both
  // modes; on scopes it to the mode on screen. Divergent stored themes force
  // it on so the state is always visible.
  const [splitModes, setSplitModes] = useState(() => colorThemeLight !== colorThemeDark);
  const split = splitModes || colorThemeLight !== colorThemeDark;

  const toggleSplit = useCallback(
    (on: boolean) => {
      if (!on) setColorTheme(activeColorTheme); // the mode on screen wins for both
      setSplitModes(on);
    },
    [activeColorTheme, setColorTheme],
  );

  const pickTheme = useCallback(
    (id: string) => {
      if (!canUseTheme(isPro, id)) {
        openPaywall("theme");
        return;
      }
      if (split) setColorThemeForMode(isDark ? "dark" : "light", id);
      else setColorTheme(id);
      track(AnalyticsEvent.ThemeChanged, { theme: id, color_mode: theme, source: "theme_picker" });
    },
    [isPro, split, isDark, setColorTheme, setColorThemeForMode, openPaywall, theme],
  );

  const pickMode = (mode: Theme) => {
    setTheme(mode);
    // Report the theme active under the NEW mode: with day/night pairing the
    // outgoing mode's id may be a different theme.
    const nextIsDark =
      mode === "dark" ||
      (mode === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    track(AnalyticsEvent.ThemeChanged, {
      theme: nextIsDark ? colorThemeDark : colorThemeLight,
      color_mode: mode,
      source: "theme_picker",
    });
  };

  return (
    <>
      <SettingsSection>
        <div className={compact ? "p-3" : ""}>
          <SegmentedControl<Theme>
            fill={compact}
            value={theme}
            onChange={pickMode}
            options={[
              { value: "light", label: t("themes.light") },
              { value: "dark", label: t("themes.dark") },
              { value: "system", label: t("themes.system") },
            ]}
          />
        </div>
        <SettingsToggleRow
          // A pill, like the segmented control above it (desktop rows only).
          className={compact ? undefined : "rounded-full pl-5 pr-2"}
          label={t("settings.separateThemes")}
          checked={split}
          onChange={toggleSplit}
        />
      </SettingsSection>
      <section className="flex flex-col gap-2.5">
        <SettingsSectionTitle>{t("settings.themes")}</SettingsSectionTitle>
        <div className={compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-2 gap-3"}>
          {themes.map((th) => (
            <ThemePreviewCard
              key={th.id}
              theme={th}
              isDark={isDark}
              active={activeColorTheme === th.id}
              locked={!canUseTheme(isPro, th.id)}
              onClick={() => pickTheme(th.id)}
            />
          ))}
        </div>
      </section>
    </>
  );
}
